<template>
  <div class="col px-0">
    <v-overlay :value="loading">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <div class="text-subtitle-1 text-sm-h6 text-md-h5 font-weight-bold text-center">
      Relatório de Produtividade e Área Plantada
    </div>

    <v-card>  
      <v-card-title>
        <span>Filtros</span>
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="6" >
            <v-autocomplete
              v-model="cultureIds"
              label="Cultura(s)"
              :items="filtersList ? filtersList.accountContext.allCulturesForAccount : []"
              item-value="id"
              item-text="name"
              clearable
              @click:clear="cultureIds = []"
              :loading="loadingFilters"
              multiple
              chips
              dense
              deletable-chips
              hint="Se nenhuma cultura for selecionada, todas serão exibidas"
              persistent-hint
            />
          </v-col>

          <v-col cols="12" md="6" >
            <v-autocomplete
              v-model="harvestIds"
              label="Safra(s)"
              :items="filtersList ? filtersList.accountContext.allHarvests : []"
              item-value="id"
              item-text="name"
              clearable
              @click:clear="harvestIds = []"
              :loading="loadingFilters"
              multiple
              chips
              dense
              deletable-chips
              hint="Se nenhuma safra for selecionada, todas serão exibidas"
              persistent-hint
            >
              <template v-slot:item="data">
                <v-list-item-content>
                  <v-list-item-title>{{data.item.name}}</v-list-item-title>
                  <v-list-item-subtitle>Status: {{data.item.statusText}}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12">
            <v-autocomplete
              v-model="periodIds"
              label="Ciclo(s)"
              :items="filtersList ? filtersList.accountContext.allPeriods : []"
              item-value="id"
              item-text="name"
              clearable
              @click:clear="periodIds = []"
              :loading="loadingFilters"
              multiple
              chips
              dense
              deletable-chips
              hint="Se nenhum ciclo for selecionado, todos serão exibidos"
              persistent-hint
            >
              <template v-slot:selection="data">
                <v-chip
                  v-bind="data.attrs"
                  :input-value="data.selected"
                  close
                  @click="data.select"
                  @click:close="removeSelectedPeriod(data.item)"
                >
                  {{ data.item.name }} 
                  <small class="font-italic font-weight-light ml-2 mt-1"> ({{data.item.additionalInfo}}) </small>
                  <small v-if="data.item.secondAdditionalInfo" class="font-italic font-weight-light ml-2 mt-1"> ({{data.item.secondAdditionalInfo}}) </small>
                </v-chip>
              </template>
              <template v-slot:item="data">
                <v-list-item-action>
                  <v-checkbox :input-value="data.attrs.inputValue"></v-checkbox>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title class="text-subtitle-1">{{data.item.name}}</v-list-item-title>
                  <v-list-item-subtitle>
                    {{data.item.additionalInfo}}
                    <br v-if="data.item.secondAdditionalInfo">
                    {{data.item.secondAdditionalInfo}}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12" md="6" >
            <v-autocomplete
              v-model="periodStatuses"
              label="Status dos Ciclos"
              :items="filtersList ? filtersList.globalContext.allPeriodStatuses : []"
              item-value="value"
              item-text="text"
              clearable
              @click:clear="periodStatuses = []"
              :loading="loadingFilters"
              multiple
              chips
              dense
              deletable-chips
              hint="Se nenhum status for selecionado, todos serão exibidos"
              persistent-hint
            />
          </v-col>

          <!-- <v-col cols="6" md="3">
            <v-autocomplete
              v-model="periodFilterModel"
              label="Período"
              :items="periodFilterOptions"
              item-value="key"
              item-text="name"
            />
          </v-col>
          <v-col cols="12" md="4" v-if="periodFilterModel == 'OTHER'">
            <DateRangeField
              :inputData.sync="periodFilterRangeModel"
              label="Período Customizado"
              :hasTodayDate="true"
            />
          </v-col> -->

          <v-col cols="6" md="2">
            <v-btn
              class="mt-3"
              @click="runFilter(false)">
              <v-icon left>mdi-magnify</v-icon>
              Filtrar
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <div>
      <BasePrint
        printableElementId="productivityReportPrintTable"
        printButtonLabel="Imprimir"
        printTitle="Relatório de Produtividade e Área Plantada"
        @start-printing="printing = loading = true"
        @stop-printing="printing = loading = false"
        class="mt-3"
      />
    </div>

    <div id="productivityReportPrintTable" v-if="productivityReport" class="mt-5">

      <v-tabs
        v-model="tab"
        centered
        icons-and-text
      >
        <v-tabs-slider></v-tabs-slider>

          <v-tab href="#tab-1">
            Gráfico
            <v-icon>mdi-chart-bar</v-icon>
          </v-tab>

          <v-tab href="#tab-2">
            Tabular
            <v-icon>mdi-table-multiple</v-icon>
          </v-tab>

          <v-tabs-items v-model="tab" class="mb-2">
            <v-tab-item value="tab-1">
              <v-row v-for="(culture, index) in productivityReport.cultures" v-bind:key="culture.culture + index">
                <v-col cols="12" sm="6" md="4">
                  <v-card>
                    <v-card-title class="py-2">
                      <span class="text-h6">Cultura</span>
                    </v-card-title>

                    <v-card-text class="text-right">
                      <span class="font-weight-bold text-subtitle-1 text-sm-h6">
                        {{culture.culture}}
                      </span>
                    </v-card-text>
                  </v-card>
                </v-col>

                <v-col cols="12" sm="6" md="4">
                  <v-card>
                    <v-card-title class="py-2">
                      <span class="text-h6">Embalagem de Comercialização</span>
                    </v-card-title>

                    <v-card-text class="text-right">
                      <span class="font-weight-bold text-subtitle-1 text-sm-h6">
                        {{culture.culturePackaging}}
                      </span>
                    </v-card-text>
                  </v-card>
                </v-col>

                <v-col cols="12" sm="6" md="4">
                  <v-card>
                    <v-card-title class="py-2">
                      <span class="text-h6">Área Plantada</span>
                    </v-card-title>

                    <v-card-text class="text-right">
                      <span class="font-weight-bold text-subtitle-1 text-sm-h6">
                        {{ culture.periodsPlantingArea | formatNumber }} ha
                      </span>
                    </v-card-text>
                  </v-card>
                </v-col>


                <v-col cols="12">
                  <line-bar-chart
                    :chartdata="getBarChartDataUnit(culture)"
                    :options="getChartOptionsUnit(culture)"
                  >
                  </line-bar-chart>
                </v-col>

                <v-col cols="12">
                  <line-bar-chart
                    :chartdata="getBarChartDataPackaging(culture)"
                    :options="getChartOptionsPackaging(culture)"
                  >
                  </line-bar-chart>
                </v-col>
                <v-divider></v-divider>
              </v-row>
            </v-tab-item>
            
            <v-tab-item value="tab-2">

              <!-- Parte tabular -->
              <v-row v-for="(culture, index) in productivityReport.cultures" v-bind:key="culture.culture + index">
                <v-col cols="12" md="8">
                  <v-card>
                    <v-card-title class="py-2">
                      <span class="text-h6">Cultura</span>
                    </v-card-title>

                    <v-card-text class="text-right">
                      <span class="font-weight-bold text-subtitle-1 text-sm-h6">
                        {{culture.culture}}
                      </span>
                    </v-card-text>
                  </v-card>
                </v-col>

                <v-col cols="12" md="4">
                  <v-card>
                    <v-card-title class="py-2">
                      <span class="text-h6">Embalagem de Comercialização</span>
                    </v-card-title>

                    <v-card-text class="text-right">
                      <span class="font-weight-bold text-subtitle-1 text-sm-h6">
                        {{culture.culturePackaging}}
                      </span>
                    </v-card-text>
                  </v-card>
                </v-col>


                <v-col cols="12">
                  <BaseIndex
                    ref="baseIndex"
                    v-if="productivityReport"
                    :loading="loading"
                    :pageLoading="pageLoading"
                    :table-headers="tableHeaders"
                    :table-items="culture.periods"
                    :maskFields="maskFields(culture.cultureUnit, culture.culturePackagingType)"
                    :enableCustomFilters="false"
                    :containTopToolbar="false"
                    :itemsPerPage="-1"
                    :disablePagination="true"
                    :indexNested="true"
                    :hasDefaultPageTitle="false"
                  >
                    <template slot="tableFooter">
                      <tr class="d-none d-md-table-row">
                        <td colspan="3" class="text-right title"><strong class="mx-3">Área Total:</strong></td>
                        <td colspan="1" class="text-left title"><strong>{{ culture.periodsPlantingArea | formatNumber }} ha </strong></td>
                        <td colspan="1" class="text-left title"><strong>{{ culture.periodsProduction | formatNumber }} {{culture.cultureUnit}}</strong></td>
                        <td colspan="1" class="text-left title"><strong>{{ culture.periodsProductivity | formatNumber }} {{culture.cultureUnit}}/ha</strong></td>
                        <td colspan="1" class="text-left title"><strong>{{ culture.periodsPackagingProductivity | formatNumber }} {{culture.culturePackagingType}}/ha</strong></td>
                      </tr>

                      <!-- for mobile only -->
                      <tr class="d-table-row d-md-none v-data-table__mobile-table-row">
                        <td class="v-data-table__mobile-row">
                          <div class="v-data-table__mobile-row__header">Área Total</div>
                          <div class="v-data-table__mobile-row__cell"><strong>{{ culture.periodsPlantingArea | formatNumber }} ha</strong></div>
                        </td>

                        <td class="v-data-table__mobile-row">
                          <div class="v-data-table__mobile-row__header">Produção Total</div>
                          <div class="v-data-table__mobile-row__cell"><strong>{{ culture.periodsProduction | formatNumber }} {{culture.cultureUnit}}</strong></div>
                        </td>

                        <td class="v-data-table__mobile-row">
                          <div class="v-data-table__mobile-row__header">Produtividade Total</div>
                          <div class="v-data-table__mobile-row__cell"><strong>{{ culture.periodsProductivity | formatNumber }} {{culture.cultureUnit}}/ha</strong></div>
                        </td>

                        <td class="v-data-table__mobile-row">
                          <div class="v-data-table__mobile-row__header">Produtividade Embalagem</div>
                          <div class="v-data-table__mobile-row__cell"><strong>{{ culture.periodsPackagingProductivity | formatNumber }} {{culture.culturePackagingType}}/ha</strong></div>
                        </td>
                      </tr>
                    </template>

                  </BaseIndex>
                </v-col>
                <v-divider></v-divider>
              </v-row>

            </v-tab-item>

          </v-tabs-items>

      </v-tabs>

      
      <v-row v-if="productivityReport">
        <v-col cols="12">
          <v-card>
            <v-card-title class="py-2">
              <span class="text-h6">Área Total Cultivada</span>
            </v-card-title>

            <v-card-text class="text-right">
              <span class="font-weight-bold text-h6">
                {{productivityReport.culturesPlantingArea | formatNumber}} ha
              </span>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

    </div>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import moment from "moment";
import BaseIndex from "@shared_components/base_index.vue";
import DateRangeField from "@shared_components/date_range_field.vue"
import BasePrint from "@shared_components/base_print.vue";
import LineBarChart from "@components/shared/charts/LineBarChart.js";

export default {
  data() {
    return {
      tab: null,
      loading: false,
      loadingFilters: false,
      pageLoading: false,
      monthFilter: moment().format('YYYY-MM'),
      cultureIds: [],
      harvestIds: [],
      periodIds: [],
      periodStatuses: [],
      productivityReport: null,
      // startDate: null,
      // endDate: null,
      filtersList: null,
      tableHeaders: [
        { text: "Data(s) do Plantio", align: "left", sortable: false, value: "plantingDates"},
        { text: "Ciclo", align: "left", sortable: false, value: "name"},
        { text: "Status", align: "left", sortable: false, value: "status"},
        { text: "Área Plantada", align: "left", sortable: false, value: "plantingArea"},
        { text: "Produção", align: "left", sortable: false, value: "production"},
        { text: "Produtividade", align: "left", sortable: false, value: "productivity"},
        { text: "Produtividade Embalagem", align: "left", sortable: false, value: "packagingProductivity"},
      ],
      // maskFields: [
      //   { field: 'plantingArea', mask: 'number' },
      //   { field: 'production', mask: 'number' },
      //   { field: 'productivity', mask: 'number' },
      // ],
      // periodFilterOptions: [
      //   { "key": "DAY", "name": "Hoje" },
      //   { "key": "WEEK", "name": "Esta Semana" },
      //   { "key": "MONTH", "name": "Este Mês" },
      //   { "key": "OTHER", "name": "Customizado" },
      // ],
      // periodFilterModel: "MONTH",
      // periodFilterRangeModel: [moment().startOf('week').format('YYYY-MM-DD'), moment().endOf('week').format('YYYY-MM-DD')]
    };
  },
  props: {
  },
  components: {
    BaseIndex: BaseIndex,
    DateRangeField: DateRangeField,
    BasePrint: BasePrint,
    LineBarChart,
  },
  created() {
    this.loadServerFilters();
  },
  mounted() {

  },
  computed: {
  }, 
  methods: {
    maskFields: function(unit, packagingType) {
      return [
        { field: 'plantingArea', mask: 'number_unit', unit: 'ha' },
        { field: 'production', mask: 'number_unit', unit: unit },
        { field: 'productivity', mask: 'number_unit', unit: unit + '/ha' },
        { field: 'packagingProductivity', mask: 'number_unit', unit: packagingType + '/ha' },
      ]
    },
    getBarChartDataUnit: function(culture) {

      let labels = culture.periods.map(period => period.name)
      return {
        labels: labels,
        datasets: [
          {
            type: 'line',
            label: 'Produtividade Média',
            unit: culture.cultureUnit,
            borderColor: '#1a3d1c',
            borderWidth: 2,
            fill: false,
            data: Array(labels.length).fill(culture.periodsProductivity),
          }, 
          {
            label: 'Produtividade',
            unit: culture.cultureUnit,
            type: 'bar',
            backgroundColor: '#4caf50',
            stack: 'Stack 0',
            data: culture.periods.map(period => period.productivity)
          },
        ]
      }
    },
    getChartOptionsUnit: function(culture) {
      return {
        maintainAspectRatio: false,
        title: {
          display: true,
          text: 'Produtividade ' + culture.cultureUnit + '/ha'
        },
        tooltips: {
          mode: 'point',
          intersect: true,
          callbacks: {
            label: function(tooltipItem, chart){
              var datasetLabel = chart.datasets[tooltipItem.datasetIndex].label || '';
              return datasetLabel + " = " + convertToNumber(tooltipItem.yLabel) + " " + (chart.datasets[tooltipItem.datasetIndex].unit || '') + "/ha";
            },
            title: function(tooltipItem) {
              // console.log(tooltipItem);
              return tooltipItem[0].xLabel;
            }
          }
        },
        responsive: true,
        scales: {
          xAxes: [
            {
              stacked: true,
              scaleLabel: {
                display: true,
                labelString: 'Ciclo'
              }
            }
          ],
          yAxes: [
            {
              scaleLabel: {
                display: true,
                labelString: 'Produtividade ' + culture.cultureUnit + "/ha"
              },
              ticks: {
                callback: function (value) {
                  // return numberToCurrency(value);
                  return convertToNumber(value);
                  // return (this.$vuetify.breakpoint.sm ? numberToAbbrviatedCurrency(value) : numberToCurrency(value))
                }
              }
            }
          ]
        },
        plugins: {
          legend: {
              display: true,
              labels: {
                  padding: 10
              },
              title: {
                padding: 10
              }
          }
        }
      }
    },
    getBarChartDataPackaging: function(culture) {

      let labels = culture.periods.map(period => period.name)
      return {
        labels: labels,
        datasets: [
          {
            type: 'line',
            label: 'Produtividade Média por ' + culture.culturePackagingTypeName,
            unit: culture.culturePackagingType,
            borderColor: '#613f00',
            borderWidth: 2,
            fill: false,
            data: Array(labels.length).fill(culture.periodsPackagingProductivity),
          }, 
          {
            label: 'Produtividade por ' + culture.culturePackagingTypeName,
            unit: culture.culturePackagingType,
            type: 'bar',
            backgroundColor: '#c48002',
            stack: 'Stack 1',
            data: culture.periods.map(period => period.packagingProductivity)
          },
        ]
      }
    },
    getChartOptionsPackaging: function(culture) {
      return {
        maintainAspectRatio: false,
        title: {
          display: true,
          text: 'Produtividade ' + culture.culturePackagingType + '/ha'
        },
        tooltips: {
          mode: 'point',
          intersect: true,
          callbacks: {
            label: function(tooltipItem, chart){
              var datasetLabel = chart.datasets[tooltipItem.datasetIndex].label || '';
              return datasetLabel + " = " + convertToNumber(tooltipItem.yLabel) + " " + (chart.datasets[tooltipItem.datasetIndex].unit || '') + "/ha";
            },
            title: function(tooltipItem) {
              // console.log(tooltipItem);
              return tooltipItem[0].xLabel;
            }
          }
        },
        responsive: true,
        scales: {
          xAxes: [
            {
              stacked: true,
              scaleLabel: {
                display: true,
                labelString: 'Ciclo'
              }
            }
          ],
          yAxes: [
            {
              scaleLabel: {
                display: true,
                labelString: 'Produtividade ' + culture.culturePackagingType + "/ha"
              },
              ticks: {
                callback: function (value) {
                  // return numberToCurrency(value);
                  return convertToNumber(value);
                  // return (this.$vuetify.breakpoint.sm ? numberToAbbrviatedCurrency(value) : numberToCurrency(value))
                }
              }
            }
          ]
        },
        plugins: {
          legend: {
              display: true,
              labels: {
                  padding: 10
              },
              title: {
                padding: 10
              }
          }
        }
      }
    },
    loadServerFilters: function() {
      this.loadingFilters = true;
      this.$apollo
        .query({
          query: LOAD_FILTERS_QUERY,
          fetchPolicy: "no-cache"
        })
        .then(response => {
          this.filtersList = response.data;
          if (this.periodStatuses.length == 0 && this.filtersList.globalContext.allPeriodStatuses.length > 0) {
            this.periodStatuses = [1,2,3];
          }
        })
        .catch(response => console.log(response))
        .finally(() => (this.loadingFilters = false));
    },
    loadServerEntries: function(firstLoading=false) {
      this.loading = true;
      this.$apollo
        .query({
          query: LOAD_PRODUCTIVITY_QUERY,
          variables: {
            cultureIds: this.cultureIds,
            harvestIds: this.harvestIds,
            periodIds: this.periodIds,
            periodStatuses: this.periodStatuses,
          },
          fetchPolicy: "no-cache"
        })
        .then(response => {
          this.productivityReport = response.data.accountContext.productivityReport;
          // console.log("this.productivityReport", this.productivityReport);
        })
        .catch(response => console.log(response))
        .finally(() => (this.loading = false));
    },
    runFilter: function(firstLoading=false) {
      // if (this.periodFilterModel != "OTHER") {
      //   this.startDate = moment().startOf(this.periodFilterModel.toLocaleLowerCase()).format('YYYY-MM-DD');
      //   this.endDate   = moment().endOf(this.periodFilterModel.toLocaleLowerCase()).format('YYYY-MM-DD');

      // } else {
      //   if (this.periodFilterRangeModel.length == 2) {
      //     this.startDate = this.periodFilterRangeModel[0];
      //     this.endDate   = this.periodFilterRangeModel[1];
      //   }
      // }
      // console.log(this.startDate, this.endDate);
      this.loadServerEntries(firstLoading);
    },
    removeSelectedPeriod: function(item) {
      const index = this.periodIds.indexOf(item.id)
      if (index >= 0) this.periodIds.splice(index, 1)
    },
  }
};

function convertToNumber(value) {
  return new Intl.NumberFormat("pt-br", {
    style: "decimal",
    minimumFractionDigits: 2,
    maximumFractionDigits: 10
  }).format(value);
}

const LOAD_FILTERS_QUERY = gql `
  {
    accountContext {
      allHarvests {
        id
        name
        status
        statusText
      }
      allPeriods {
        id
        name
        status
        statusText
        additionalInfo
        secondAdditionalInfo
      }
      allCulturesForAccount {
        id
        name
      }
    }
    globalContext {
      allPeriodStatuses
    }
  }
`;

const LOAD_PRODUCTIVITY_QUERY = gql `
query ($harvestIds: [ID!]!, $periodIds: [ID!]!, $cultureIds: [ID!]!, $periodStatuses: [ID!]!) {
  accountContext {
    productivityReport(cultureIds: $cultureIds, harvestIds: $harvestIds, periodIds: $periodIds, periodStatuses: $periodStatuses) 
  }
}
`;

</script>

<style scoped>
</style>
